import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "../../config";
import VerificationModal from "../../components/Common/VerificationModal"; 
import AuthHeader from "./AuthHeader";  
import AuthFooter from "./AuthFooter";

const LoginForm = ({ onLoginSubmit, isSubmitting }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isCaptchaLoaded, setIsCaptchaLoaded] = useState(false); 

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: ""
  });

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaLoaded = () => {
    setIsCaptchaLoaded(true); 
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 1. Validasi Input Kosong
    if (!formData.email.trim() || !formData.password.trim()) {
      setModalConfig({
        isOpen: true,
        title: "Input Tidak Lengkap",
        message: "Harap masukkan Email dan Password Anda sebelum melanjutkan."
      });
      return;
    }

    // 2. Validasi reCAPTCHA
    if (!captchaToken) {
      setModalConfig({
        isOpen: true,
        title: "Verifikasi Keamanan",
        message: "Maaf, Anda harus mencentang kotak 'I'm not a robot' untuk melanjutkan.",
        buttonText: "Lanjut Verifikasi reCAPTCHA"
      });
      return;
    }

    // 3. Jika valid, kirim ke fungsi login utama di LoginPage
    onLoginSubmit(formData.email, formData.password, captchaToken);
  };

  return (
    <>
      <VerificationModal 
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
        title={modalConfig.title}
        message={modalConfig.message}
      />

      <div className="py-16 px-10 md:py-24 md:px-16 rounded-[1.0rem] shadow-2xl w-full max-w-2xl border border-white/10 transition-all mx-auto">
        <AuthHeader 
          title="Login Account" 
          description="Masukkan email dan password untuk masuk ke dashboard Anda." 
        />

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Email Field */}
          <div className="space-y-3">
            <label className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">
              Email Address
            </label>
            <input
              type="email"
              required
              placeholder="name@company.com"
              className="w-full px-6 py-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/20 transition-all font-medium text-slate-900 dark:text-white"
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          {/* Password Field */}
          <div className="space-y-3">
            <div className="flex justify-between items-center px-1">
              <label className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
                Password
              </label>
              <Link to="/forgot-password" intrinsic="sm" className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-bold">
                Lupa Password?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                placeholder="••••••••"
                className="w-full px-6 py-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/20 transition-all font-medium text-slate-900 dark:text-white"
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2.5 bg-[#004A8D] text-white rounded-xl hover:bg-[#003566] transition-all shadow-md active:scale-90"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>    

          {/* reCAPTCHA */}
          <div className="py-2 flex flex-col items-center overflow-hidden min-h-[80px] justify-center">
            {!isCaptchaLoaded && (
              <div className="flex flex-col items-center gap-2 text-slate-400 animate-pulse">
                <Loader2 className="animate-spin" size={24} />
                <span className="text-[10px] font-bold uppercase tracking-widest">Memuat Keamanan...</span>
              </div>
            )}
            
            <div className={`scale-110 md:scale-125 my-4 transition-opacity duration-500 ${isCaptchaLoaded ? "opacity-100" : "opacity-0 absolute"}`}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                asyncScriptOnLoad={handleCaptchaLoaded}
                theme="light" 
                size="normal"
                type="image"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#004A8D] hover:bg-[#003566] text-white font-black py-5 rounded-2xl transition-all duration-300 shadow-xl shadow-blue-900/30 uppercase tracking-[0.2em] text-xs active:scale-[0.98] disabled:opacity-50"
          >
            {isSubmitting ? "Memproses..." : "Login Sekarang"}
          </button>
        </form>
        
        <AuthFooter 
          question="Belum memiliki akun?" 
          linkText="Daftar Sekarang" 
          linkTo="/register" 
        />
      </div>
    </>
  );
};

export default LoginForm;